"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { useAuth } from "@/hooks/use-auth"
import { Loader2, Plus, SlidersHorizontal, Trash2 } from "lucide-react"
import type { ChannelConfigDTO, ChannelMatcherDTO, ChannelSourceDTO } from "@/types/channel-config"
import type { UrlParamAliasSet } from "@/lib/attribution/url-param-aliases"
import { UrlParamAliasesDialog } from "./url-param-aliases-dialog"
import { MatcherRow } from "./matcher-row"
import { DisableGroupsConfirm } from "./disable-groups-confirm"
import { detectMatcherMode, getNextGroupId, splitMatcherGroups, type IndexedMatcher } from "@/lib/attribution/matcher-groups"
import { useTranslations } from "next-intl"




// Modal that allows users to curate attribution channels and matching rules.

interface ChannelSourceForm extends Omit<ChannelSourceDTO, "matchers"> {
  localId: string
  matchers: ChannelMatcherDTO[]
}

interface ChannelForm extends Omit<ChannelConfigDTO, "sources" | "matchers"> {
  localId: string
  sources: ChannelSourceForm[]
}

interface ChannelConfigDialogProps {
  integrationId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfigSaved?: () => void
}

const createLocalId = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `tmp-${Math.random().toString(36).slice(2, 10)}`
}

const normalizeSlug = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

const blankMatcher = (order: number): ChannelMatcherDTO => ({
  id: undefined,
  field: "utm_source",
  operator: "contains",
  value: "",
  order,
  isAnyValue: false,
  matcherGroupId: null,
  isNegated: false,
})

const createDefaultSource = (order: number): ChannelSourceForm => ({
  localId: createLocalId(),
  id: undefined,
  slug: normalizeSlug(`source-${order + 1}`),
  label: "New Source",
  order,
  matchers: [blankMatcher(0)],
})

const mapSourceToForm = (source: ChannelSourceDTO | undefined, fallbackOrder: number): ChannelSourceForm => {
  const sanitizedSlug = normalizeSlug(source?.slug || source?.label || `source-${fallbackOrder + 1}`)
  const baseMatchers =
    source?.matchers?.map((matcher, matcherIndex) => ({
      ...matcher,
      order: matcher.order ?? matcherIndex,
    })) ?? []

  const matchers = baseMatchers.length > 0 ? baseMatchers : [blankMatcher(0)]

  return {
    id: source?.id,
    localId: createLocalId(),
    slug: sanitizedSlug,
    label: source?.label || `Source ${fallbackOrder + 1}`,
    order: source?.order ?? fallbackOrder,
    matchers,
  }
}

const mapChannelToForm = (channel: ChannelConfigDTO): ChannelForm => {
  const sources = (channel.sources || []).map((source, index) => mapSourceToForm(source, index))

  return {
    id: channel.id,
    localId: createLocalId(),
    slug: channel.slug,
    label: channel.label,
    color: channel.color ?? "",
    priority: channel.priority ?? 0,
    allowChannelLevelExpense: channel.allowChannelLevelExpense,
    isFallback: Boolean(channel.isFallback),
    isSpecial: Boolean(channel.isSpecial),
    sources,
  }
}

const sortFormsByPriority = (channels: ChannelForm[]): ChannelForm[] =>
  [...channels].sort(
    (a, b) =>
      (a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER),
  )

const resequenceChannelPriorities = (channels: ChannelForm[]): ChannelForm[] => {
  const nonFallback = channels.filter((channel) => !channel.isFallback)
  const fallback = channels.filter((channel) => channel.isFallback)
  const ordered = [...nonFallback, ...fallback]

  return ordered.map((channel, index) => ({
    ...channel,
    priority: index + 1,
  }))
}

const prepareChannelForms = (channels: ChannelConfigDTO[]): ChannelForm[] => {
  const sortedChannels = [...channels].sort(
    (a, b) =>
      (a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER),
  )

  const orderedChannels = [
    ...sortedChannels.filter((channel) => !channel.isFallback),
    ...sortedChannels.filter((channel) => channel.isFallback),
  ]

  const mapped = orderedChannels.map(mapChannelToForm)
  return resequenceChannelPriorities(mapped)
}

export function ChannelConfigDialog({ integrationId, open, onOpenChange, onConfigSaved }: ChannelConfigDialogProps) {


  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardDialogsChannelConfigDialog")
  const tCommonUi = useTranslations("Common")

  const { toast } = useToast()
  const { fetchWithAuth } = useAuth()
  const [channels, setChannels] = useState<ChannelForm[]>([])
  const [paramAliasSets, setParamAliasSets] = useState<UrlParamAliasSet[] | null>(null)
  const [isAliasDialogOpen, setIsAliasDialogOpen] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [shouldScrollToEnd, setShouldScrollToEnd] = useState(false)
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [disableGroupsTarget, setDisableGroupsTarget] = useState<{ channelLocalId: string; sourceLocalId: string } | null>(null)
  const lastSourceRef = useRef<HTMLDivElement | null>(null)

  const selectedChannel = useMemo(
    () => channels.find((channel) => channel.localId === selectedChannelId) ?? channels[0],
    [channels, selectedChannelId],
  )

  const nonFallbackCount = useMemo(
    () => channels.filter((channel) => !channel.isFallback).length,
    [channels],
  )

  const priorityOptions = useMemo(
    () => Array.from({ length: nonFallbackCount }, (_, index) => index + 1),
    [nonFallbackCount],
  )

  useEffect(() => {
    if (shouldScrollToEnd && lastSourceRef.current) {
      lastSourceRef.current.scrollIntoView({ behavior: "smooth", block: "end" })
      setShouldScrollToEnd(false)
    }
  }, [selectedChannel?.sources.length, shouldScrollToEnd])

  useEffect(() => {
    setShouldScrollToEnd(false)
  }, [selectedChannelId])

  useEffect(() => {
    if (!open) {
      setIsResetDialogOpen(false)
      setIsResetting(false)
      setIsAliasDialogOpen(false)
      setDisableGroupsTarget(null)
    }
  }, [open])

  useEffect(() => {
    if (!open || !integrationId) return

    const controller = new AbortController()

    const fetchConfig = async () => {
      try {
        setIsLoading(true)
        // Load the persisted channel set; falls back to defaults in the API.
        const response = await fetchWithAuth(
          `/api/integrations/shopify/channel-config?integration_id=${integrationId}`,
          { signal: controller.signal },
        )

        if (!response.ok) {
          throw new Error("Failed to load channel configuration")
        }

        const data = await response.json()
        const mapped = prepareChannelForms(data.channels as ChannelConfigDTO[])

        setChannels(mapped)
        setParamAliasSets((data.paramAliasSets as UrlParamAliasSet[] | null) ?? null)
        setSelectedChannelId(mapped[0]?.localId ?? null)
        setLoadFailed(false)
      } catch (error) {
        if ((error as Error).name === "AbortError") return
        // Saving now would persist defaults over the stored config, so both writes stay locked.
        setLoadFailed(true)
        console.error("[Channel Config] Failed to fetch:", error)
        toast({
          title: tUi("failedToLoadChannelConfiguration"),
          description: error instanceof Error ? error.message : tUi("unexpectedError"),
          variant: "destructive",
        })
      } finally {
        setIsLoading(false)
      }
    }

    fetchConfig()

    return () => controller.abort()
  }, [open, integrationId, toast, fetchWithAuth, tUi])

  const handleResetToDefault = async () => {
    if (!integrationId) {
      toast({
        title: tUi("selectAStore"),
        description: tUi("chooseIntegrationBeforeReset"),
        variant: "destructive",
      })
      return
    }

    try {
      setIsResetting(true)
      const response = await fetchWithAuth(`/api/integrations/shopify/channel-config?integration_id=${integrationId}`, {
        method: "DELETE",
      })
      const data = await response.json().catch(() => null)

      if (!response.ok) {
        const message =
          data && typeof data === "object" && "error" in data && typeof data.error === "string"
            ? data.error
            : tUi("failedToResetChannelConfiguration")
        throw new Error(message)
      }

      const mapped = prepareChannelForms(((data || {}).channels ?? []) as ChannelConfigDTO[])

      setChannels(mapped)
      setParamAliasSets(null)
      setSelectedChannelId(mapped[0]?.localId ?? null)
      toast({
        title: tUi("channelConfigurationReset"),
        description: tUi("defaultsRestored"),
      })
      onConfigSaved?.()
      setIsResetDialogOpen(false)
    } catch (error) {
      console.error("[Channel Config] Failed to reset:", error)
      toast({
        title: tUi("failedToResetChannelConfiguration"),
        description: error instanceof Error ? error.message : "Unexpected error",
        variant: "destructive",
      })
    } finally {
      setIsResetting(false)
    }
  }

  const updateChannel = (localId: string, updater: (channel: ChannelForm) => ChannelForm) => {
    setChannels((prev) => prev.map((channel) => (channel.localId === localId ? updater(channel) : channel)))
  }

  const updateSource = (
    channelLocalId: string,
    sourceLocalId: string,
    updater: (source: ChannelSourceForm) => ChannelSourceForm,
  ) => {
    updateChannel(channelLocalId, (channel) => {
      const nextSources = channel.sources.map((source, index) => {
        if (source.localId !== sourceLocalId) {
          return { ...source, order: index }
        }

        const updated = updater(source)
        return { ...updated, order: index }
      })

      return {
        ...channel,
        sources: nextSources,
      }
    })
  }

  const handleAddSource = (channelLocalId: string) => {
    updateChannel(channelLocalId, (channel) => {
      const nextSources = [...channel.sources, createDefaultSource(channel.sources.length)]
      return {
        ...channel,
        sources: nextSources.map((source, index) => ({ ...source, order: index })),
      }
    })
    setShouldScrollToEnd(true)
  }

  const handleRemoveSource = (channelLocalId: string, sourceLocalId: string) => {
    updateChannel(channelLocalId, (channel) => {
      const remaining = channel.sources.filter((source) => source.localId !== sourceLocalId)
      return {
        ...channel,
        sources: remaining.map((source, index) => ({ ...source, order: index })),
      }
    })
  }

  const handleSourceLabelChange = (channelLocalId: string, sourceLocalId: string, value: string) => {
    const nextLabel = value
    updateSource(channelLocalId, sourceLocalId, (source) => {
      const normalized = normalizeSlug(nextLabel)
      const fallbackSlug = source.slug || normalizeSlug(source.label) || `source-${source.order + 1}`
      return {
        ...source,
        label: nextLabel,
        slug: normalized || fallbackSlug,
      }
    })
  }

  const handleAddChannel = () => {
    const newChannel: ChannelForm = {
      localId: createLocalId(),
      slug: normalizeSlug(`channel-${channels.length + 1}`),
      label: tUi("newChannel"),
      color: "",
      priority: channels.length + 1,
      allowChannelLevelExpense: true,
      isFallback: false,
      isSpecial: false,
      sources: [createDefaultSource(0)],
    }

    setChannels((prev) => {
      const next = [...prev]
      const fallbackIndex = next.findIndex((channel) => channel.isFallback)
      if (fallbackIndex >= 0) {
        next.splice(fallbackIndex, 0, newChannel)
      } else {
        next.push(newChannel)
      }
      return resequenceChannelPriorities(next)
    })
    setSelectedChannelId(newChannel.localId)
  }

  const handleDeleteChannel = (localId: string) => {
    const target = channels.find((channel) => channel.localId === localId)
    if (!target) return
    if (target.isSpecial) {
      toast({
        title: tUi("cannotDeleteInfluencerChannel"),
        description: tUi("theInfluencersChannelIsRequiredAndCannotBe"),
        variant: "destructive",
      })
      return
    }
    if (target.isFallback) {
      toast({
        title: tUi("cannotDeleteFallbackChannel"),
        description: tUi("atLeastOneFallbackChannelIsRequired"),
        variant: "destructive",
      })
      return
    }

    setChannels((prev) => {
      const remaining = prev.filter((channel) => channel.localId !== localId)
      const resequenced = resequenceChannelPriorities(remaining)
      if (selectedChannelId === localId) {
        setSelectedChannelId(resequenced[0]?.localId ?? null)
      }
      return resequenced
    })
  }

  const handleAddMatcher = (channelLocalId: string, sourceLocalId: string) => {
    updateSource(channelLocalId, sourceLocalId, (source) => {
      const nextMatchers = [...source.matchers, blankMatcher(source.matchers.length)].map((matcher, index) => ({
        ...matcher,
        order: index,
      }))

      return {
        ...source,
        matchers: nextMatchers,
      }
    })
  }

  const handleMatcherChange = (
    channelLocalId: string,
    sourceLocalId: string,
    matcherIndex: number,
    key: "field" | "operator" | "value",
    value: string,
  ) => {
    updateSource(channelLocalId, sourceLocalId, (source) => ({
      ...source,
      matchers: source.matchers.map((matcher, index) => (index === matcherIndex ? { ...matcher, [key]: value } : matcher)),
    }))
  }

  const handleMatcherAnyValueToggle = (
    channelLocalId: string,
    sourceLocalId: string,
    matcherIndex: number,
    checked: boolean,
  ) => {
    updateSource(channelLocalId, sourceLocalId, (source) => ({
      ...source,
      matchers: source.matchers.map((matcher, index) => {
        if (index !== matcherIndex) return matcher
        return {
          ...matcher,
          isAnyValue: checked,
          value: checked ? "" : matcher.value,
        }
      }),
    }))
  }

  const handleMatcherNegatedToggle = (
    channelLocalId: string,
    sourceLocalId: string,
    matcherIndex: number,
    checked: boolean,
  ) => {
    updateSource(channelLocalId, sourceLocalId, (source) => ({
      ...source,
      matchers: source.matchers.map((matcher, index) => {
        if (index !== matcherIndex) return matcher
        return {
          ...matcher,
          isNegated: checked,
        }
      }),
    }))
  }

  const handleRemoveMatcher = (channelLocalId: string, sourceLocalId: string, matcherIndex: number) => {
    updateSource(channelLocalId, sourceLocalId, (source) => ({
      ...source,
      matchers: source.matchers
        .filter((_, index) => index !== matcherIndex)
        .map((matcher, index) => ({ ...matcher, order: index })),
    }))
  }

  // Existing matchers keep their ids: on a legacy source they become the base rule of the new group.
  const handleAddMatcherGroup = (channelLocalId: string, sourceLocalId: string) => {
    updateSource(channelLocalId, sourceLocalId, (source) => ({
      ...source,
      matchers: [...source.matchers, { ...blankMatcher(source.matchers.length), matcherGroupId: getNextGroupId(source.matchers) }],
    }))
  }

  const handleRemoveMatcherGroup = (channelLocalId: string, sourceLocalId: string, groupId: number) => {
    updateSource(channelLocalId, sourceLocalId, (source) => ({
      ...source,
      matchers: source.matchers
        .filter(m => m.matcherGroupId !== groupId)
        .map((matcher, index) => ({ ...matcher, order: index })),
    }))
  }

  const handleAddMatcherToGroup = (channelLocalId: string, sourceLocalId: string, groupId: number) => {
    updateSource(channelLocalId, sourceLocalId, (source) => ({
      ...source,
      matchers: [...source.matchers, { ...blankMatcher(source.matchers.length), matcherGroupId: groupId }],
    }))
  }

  // Merges the base rule and every group into one legacy rule; confirmed first because alternatives collapse.
  const handleDisableGroupedMode = (channelLocalId: string, sourceLocalId: string) => {
    updateSource(channelLocalId, sourceLocalId, (source) => ({
      ...source,
      matchers: source.matchers.map((matcher) => ({ ...matcher, matcherGroupId: null })),
    }))
  }

  const renderMatcherRows = (
    channelLocalId: string,
    source: ChannelSourceForm,
    entries: IndexedMatcher<ChannelMatcherDTO>[],
    idPrefix: string,
    className?: string,
  ) =>
    entries.map(({ matcher, index }) => (
      <MatcherRow
        key={`${idPrefix}-${index}`}
        idPrefix={`${idPrefix}-${index}`}
        matcher={matcher}
        className={className}
        onChange={(key, value) => handleMatcherChange(channelLocalId, source.localId, index, key, value)}
        onNegatedToggle={(checked) => handleMatcherNegatedToggle(channelLocalId, source.localId, index, checked)}
        onAnyValueToggle={(checked) => handleMatcherAnyValueToggle(channelLocalId, source.localId, index, checked)}
        onRemove={() => handleRemoveMatcher(channelLocalId, source.localId, index)}
      />
    ))

  const renderSourceMatchers = (channelLocalId: string, source: ChannelSourceForm) => {
    if (source.matchers.length === 0) {
      return (
        <p className="rounded-md border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">
          <span key={tUi("addAtLeastOneMatcherSoThisSource")}>{tUi("addAtLeastOneMatcherSoThisSource")}</span></p>
      )
    }

    if (detectMatcherMode(source.matchers) === "legacy") {
      const all = source.matchers.map((matcher, index) => ({ matcher, index }))
      return (
        <>
          <div className="space-y-3">{renderMatcherRows(channelLocalId, source, all, `${source.localId}-matcher`)}</div>
          <p className="text-xs text-muted-foreground">
            <span key={tUi("addMultipleRowsWithTheSameFieldIf")}>{tUi("addMultipleRowsWithTheSameFieldIf")}</span></p>
        </>
      )
    }

    const { baseRule, groups } = splitMatcherGroups(source.matchers)
    const canRemoveGroup = groups.size > 1 || baseRule.length > 0
    return (
      <div className="space-y-4">
        <div className="space-y-3 rounded-lg border-2 border-dashed p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs font-mono"><span key={tUi("baseRule")}>{tUi("baseRule")}</span></Badge>
              <span className="text-xs text-muted-foreground">
                <span key={tUi("allFieldsMustMatchSeveralValuesForOne")}>{tUi("allFieldsMustMatchSeveralValuesForOne")}</span></span>
            </div>
            <Button size="sm" variant="outline" onClick={() => handleAddMatcher(channelLocalId, source.localId)}>
              <Plus className="mr-1 h-3 w-3" /> <span><span key={tUi("addMatcher")}>{tUi("addMatcher")}</span></span>
            </Button>
          </div>
          {baseRule.length === 0 ? (
            <p className="text-xs text-muted-foreground"><span key={tUi("noBaseRuleOnlyTheGroupsBelowApply")}>{tUi("noBaseRuleOnlyTheGroupsBelowApply")}</span></p>
          ) : (
            <div className="space-y-2">{renderMatcherRows(channelLocalId, source, baseRule, `${source.localId}-base`, "bg-background")}</div>
          )}
        </div>

        {Array.from(groups.entries()).map(([groupId, entries], groupIdx) => (
          <div key={`${source.localId}-group-${groupId}`}>
            {(baseRule.length > 0 || groupIdx > 0) && (
              <div className="flex items-center justify-center py-2">
                <div className="flex items-center gap-2">
                  <div className="h-px w-12 bg-border"></div>
                  <Badge variant="outline" className="bg-background font-bold text-blue-600"><span key={tUi("oR")}>{tUi("oR")}</span></Badge>
                  <div className="h-px w-12 bg-border"></div>
                </div>
              </div>
            )}
            <div className="space-y-3 rounded-lg border-2 border-blue-200 bg-blue-50/50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs font-mono">
                    <span><span key={tUi("group")}>{tUi("group")}</span></span> {groupId}
                  </Badge>
                  <span className="text-xs text-muted-foreground"><span key={tUi("allMatchersMustMatch")}>{tUi("allMatchersMustMatch")}</span></span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleAddMatcherToGroup(channelLocalId, source.localId, groupId)}>
                    <Plus className="mr-1 h-3 w-3" /> <span><span key={tUi("addMatcher")}>{tUi("addMatcher")}</span></span>
                  </Button>
                  {canRemoveGroup && (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={tUi("removeGroupValue0", { value0: String(groupId) })}
                      onClick={() => handleRemoveMatcherGroup(channelLocalId, source.localId, groupId)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-2">{renderMatcherRows(channelLocalId, source, entries, `${source.localId}-group-${groupId}`, "bg-background")}</div>
            </div>
          </div>
        ))}

        <p className="text-xs text-muted-foreground">
          <span key={tUi("anOrderMatchesThisSourceWhenTheBase")}>{tUi("anOrderMatchesThisSourceWhenTheBase")}</span></p>
      </div>
    )
  }

  const handleToggleFallback = (localId: string, checked: boolean) => {
    setChannels((prev) => {
      const updated = prev.map((channel) => {
        if (channel.localId === localId) {
          const nextSlug = checked
            ? "other"
            : channel.isSpecial
              ? channel.slug
              : normalizeSlug(channel.label) || channel.slug
          return { ...channel, isFallback: checked, slug: nextSlug }
        }
        return checked ? { ...channel, isFallback: false } : channel
      })

      if (checked) {
        const sorted = sortFormsByPriority(updated)
        const currentIndex = sorted.findIndex((channel) => channel.localId === localId)
        if (currentIndex === -1) {
          return resequenceChannelPriorities(updated)
        }
        const [targetChannel] = sorted.splice(currentIndex, 1)
        sorted.push(targetChannel)
        return resequenceChannelPriorities(sorted)
      }

      return resequenceChannelPriorities(updated)
    })
  }

  const handleChannelPriorityChange = (localId: string, nextPriority: number) => {
    setChannels((prev) => {
      const nonFallback = prev.filter((channel) => !channel.isFallback)
      const fallback = prev.filter((channel) => channel.isFallback)
      const currentIndex = nonFallback.findIndex((channel) => channel.localId === localId)
      if (currentIndex === -1) {
        return prev
      }

      const clampedPriority = Math.max(1, Math.min(nextPriority, nonFallback.length))
      const [targetChannel] = nonFallback.splice(currentIndex, 1)
      nonFallback.splice(clampedPriority - 1, 0, targetChannel)

      return resequenceChannelPriorities([...nonFallback, ...fallback])
    })
  }

  const handleSave = async () => {
    if (!integrationId) {
      toast({
        title: tUi("selectAStore"),
        description: tUi("chooseAShopifyIntegrationBeforeSavingChannelSettings"),
        variant: "destructive",
      })
      return
    }

    try {
      setIsSaving(true)
      // Serialize the local form state into the API contract.
      const sortedChannels = sortFormsByPriority(channels)
      const payloadChannels = sortedChannels.map((channel) => {
        const sanitizedSources = channel.sources
          .map((source, sourceIndex) => {
            const seenAnyValueFields = new Set<string>()
            const sanitizedMatchers = source.matchers
              .map((matcher, matcherIndex) => {
                const trimmedValue = matcher.value.trim()
                const isAnyValue = Boolean(matcher.isAnyValue)

                if (isAnyValue) {
                  if (seenAnyValueFields.has(matcher.field)) {
                    return null
                  }
                  seenAnyValueFields.add(matcher.field)
                } else if (trimmedValue.length === 0) {
                  return null
                }

                return {
                  id: matcher.id,
                  field: matcher.field,
                  operator: matcher.operator,
                  value: trimmedValue,
                  order: matcher.order ?? matcherIndex,
                  isAnyValue,
                  matcherGroupId: matcher.matcherGroupId ?? null,
                  isNegated: Boolean(matcher.isNegated),
                }
              })
              .filter((matcher) => Boolean(matcher)) as ChannelMatcherDTO[]

            if (sanitizedMatchers.length === 0) {
              return null
            }

            return {
              id: source.id,
              slug: normalizeSlug(source.slug || source.label || `source-${sourceIndex + 1}`),
              label: source.label.trim() || `Source ${sourceIndex + 1}`,
              order: sourceIndex,
              matchers: sanitizedMatchers,
            }
          })
          .filter((source) => Boolean(source)) as ChannelSourceDTO[]

        return {
          id: channel.id,
          slug: normalizeSlug(channel.slug || channel.label),
          label: channel.label.trim(),
          color: channel.color || null,
          priority: channel.priority,
          allowChannelLevelExpense: channel.allowChannelLevelExpense,
          isFallback: channel.isFallback,
          isSpecial: channel.isSpecial,
          matchers: [],
          sources: sanitizedSources,
        }
      })

      const response = await fetchWithAuth(`/api/integrations/shopify/channel-config?integration_id=${integrationId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        // paramAliasSets omitted on purpose: aliases own their save path, absent = keep stored.
        body: JSON.stringify({ channels: payloadChannels }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || "Failed to save channel configuration")
      }

      const data = await response.json()
      const mapped = prepareChannelForms(data.channels as ChannelConfigDTO[])

      setChannels(mapped)
      setParamAliasSets((data.paramAliasSets as UrlParamAliasSet[] | null) ?? null)
      setSelectedChannelId(mapped[0]?.localId ?? null)

      toast({ title: tUi("channelConfigurationSaved") })
      onConfigSaved?.()
      onOpenChange(false)
    } catch (error) {
      console.error("[Channel Config] Failed to save:", error)
      toast({
        title: tUi("failedToSaveChannelConfiguration"),
        description: error instanceof Error ? error.message : tUi("unexpectedError"),
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle><span key={tUi("manageChannelsSources")}>{tUi("manageChannelsSources")}</span></DialogTitle>
          <DialogDescription>
            <span key={tUi("channelsAreEvaluatedInPriorityOrderChooseLower")}>{tUi("channelsAreEvaluatedInPriorityOrderChooseLower")}</span></DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex h-72 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground"><span key={tUi("loadingConfiguration")}>{tUi("loadingConfiguration")}</span></span>
          </div>
        ) : channels.length === 0 ? (
          <div className="flex h-72 flex-col items-center justify-center gap-3 text-center text-sm text-muted-foreground">
            <p><span key={tUi("noChannelsConfiguredYet")}>{tUi("noChannelsConfiguredYet")}</span></p>
            <Button onClick={handleAddChannel} size="sm">
              <Plus className="mr-2 h-4 w-4" /> <span><span key={tUi("addChannel")}>{tUi("addChannel")}</span></span>
            </Button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-[250px_1fr]">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-muted-foreground"><span key={tUi("channels")}>{tUi("channels")}</span></h3>
                <Button onClick={handleAddChannel} size="icon" variant="outline">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <ScrollArea className="h-[60vh] rounded-md border pr-3">
                <div className="space-y-1 p-2">
                  {channels.map((channel) => (
                    <button
                      key={channel.localId}
                      type="button"
                      onClick={() => setSelectedChannelId(channel.localId)}
                      className={`w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${selectedChannel?.localId === channel.localId ? "bg-muted" : "hover:bg-muted/70"}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono tracking-tight">
                            {channel.priority}
                          </Badge>
                          <span className="font-medium">{channel.label}</span>
                        </div>
                        <div className="flex gap-1">
                          {channel.isSpecial && <Badge variant="secondary"><span key={tUi("special")}>{tUi("special")}</span></Badge>}
                          {channel.isFallback && <Badge variant="outline"><span key={tUi("fallback")}>{tUi("fallback")}</span></Badge>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
              <div>
                <AlertDialog
                  open={isResetDialogOpen}
                  onOpenChange={(nextOpen) => {
                    if (isResetting) {
                      return
                    }
                    setIsResetDialogOpen(nextOpen)
                  }}
                >
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      disabled={isLoading || isSaving || isResetting}
                    >
                      <span key={tUi("resetToDefault")}>{tUi("resetToDefault")}</span></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle><span key={tUi("resetChannelConfiguration")}>{tUi("resetChannelConfiguration")}</span></AlertDialogTitle>
                      <AlertDialogDescription>
                        <span key={tUi("thisWillDeleteAllSavedChannelsSourcesAnd")}>{tUi("thisWillDeleteAllSavedChannelsSourcesAnd")}</span></AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={isResetting}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
                      <AlertDialogAction
                        disabled={isResetting}
                        onClick={(event) => {
                          event.preventDefault()
                          void handleResetToDefault()
                        }}
                      >
                        {isResetting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> <span key={tUi("resetting")}>{tUi("resetting")}</span></>
                        ) : (
                          tUi("resetToDefault")
                        )}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            {selectedChannel ? (
              <ScrollArea className="h-[70vh] pr-4">
                <div className="space-y-6 pr-2">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor="channel-label"><span key={tUi("channelLabel")}>{tUi("channelLabel")}</span></Label>
                      <Input
                        id="channel-label"
                        value={selectedChannel.label}
                        onChange={(event) =>
                          updateChannel(selectedChannel.localId, (channel) => ({
                            ...channel,
                            label: event.target.value,
                            slug:
                              channel.isSpecial || channel.isFallback
                                ? channel.slug
                                : normalizeSlug(event.target.value) || channel.slug,
                          }))
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        <span><span key={tUi("internalID")}>{tUi("internalID")}</span></span> <code>{selectedChannel.slug}</code>
                      </p>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="channel-color"><span key={tUi("color")}>{tUi("color")}</span></Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="channel-color"
                          value={selectedChannel.color ?? ""}
                          placeholder="#1a73e8"
                          onChange={(event) =>
                            updateChannel(selectedChannel.localId, (channel) => ({
                              ...channel,
                              color: event.target.value,
                            }))
                          }
                        />
                        <input
                          type="color"
                          aria-label={tUi("pickChannelColor")}
                          value={
                            selectedChannel.color && /^#[0-9a-fA-F]{6}$/.test(selectedChannel.color)
                              ? selectedChannel.color
                              : "#1a73e8"
                          }
                          className="h-10 w-10 cursor-pointer rounded border border-input bg-background p-1 shadow-sm"
                          onChange={(event) =>
                            updateChannel(selectedChannel.localId, (channel) => ({
                              ...channel,
                              color: event.target.value,
                            }))
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            updateChannel(selectedChannel.localId, (channel) => ({
                              ...channel,
                              color: "",
                            }))
                          }
                        >
                          <span key={tUi("clear")}>{tUi("clear")}</span></Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        <span key={tUi("setAHexColorOrUseThePicker")}>{tUi("setAHexColorOrUseThePicker")}</span></p>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="channel-priority"><span key={tUi("priority")}>{tUi("priority")}</span></Label>
                      <Select
                        value={String(selectedChannel.priority)}
                        onValueChange={(value) =>
                          handleChannelPriorityChange(selectedChannel.localId, Number(value))
                        }
                        disabled={selectedChannel.isFallback}
                      >
                        <SelectTrigger id="channel-priority">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {priorityOptions.map((option) => (
                            <SelectItem key={option} value={String(option)}>
                              <span><span key={tUi("priority")}>{tUi("priority")}</span></span> {option}
                            </SelectItem>
                          ))}
                          {selectedChannel.isFallback && (
                            <SelectItem value={String(selectedChannel.priority)}>
                              <span><span key={tUi("priority")}>{tUi("priority")}</span></span> {selectedChannel.priority}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <p key={selectedChannel.isFallback
                          ? tUi("theFallbackChannelAlwaysRunsAfterEveryOther")
                          : tUi("lowerNumbersAreEvaluatedFirstWhenAnOrder")} className="text-xs text-muted-foreground">
                        {selectedChannel.isFallback
                          ? tUi("theFallbackChannelAlwaysRunsAfterEveryOther")
                          : tUi("lowerNumbersAreEvaluatedFirstWhenAnOrder")}
                      </p>
                    </div>
                    <div className="grid gap-2">
                      <Label><span key={tUi("allowChannelLevelExpenses")}>{tUi("allowChannelLevelExpenses")}</span></Label>
                      <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                        <Switch
                          checked={selectedChannel.allowChannelLevelExpense}
                          onCheckedChange={(checked) =>
                            updateChannel(selectedChannel.localId, (channel) => ({
                              ...channel,
                              allowChannelLevelExpense: checked,
                            }))
                          }
                        />
                        <span className="text-sm"><span key={tUi("enableBudgetingDirectlyOnTheChannel")}>{tUi("enableBudgetingDirectlyOnTheChannel")}</span></span>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label><span key={tUi("channelType")}>{tUi("channelType")}</span></Label>
                    <div className="flex flex-col gap-2 rounded-md border px-3 py-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm"><span key={tUi("fallbackChannel")}>{tUi("fallbackChannel")}</span></span>
                        <Switch
                          checked={selectedChannel.isFallback}
                          disabled={selectedChannel.isSpecial || selectedChannel.slug === "influencers"}
                          onCheckedChange={(checked) => handleToggleFallback(selectedChannel.localId, checked)}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        <span key={tUi("influencerChannelIsAlwaysAvailableExactlyOneFallback")}>{tUi("influencerChannelIsAlwaysAvailableExactlyOneFallback")}</span></p>
                    </div>
                  </div>

                  {!selectedChannel.isSpecial && !selectedChannel.isFallback && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-muted-foreground"><span key={tUi("sourcesMatchers")}>{tUi("sourcesMatchers")}</span></h4>
                        <Button size="sm" variant="outline" onClick={() => handleAddSource(selectedChannel.localId)}>
                          <Plus className="mr-2 h-4 w-4" /> <span><span key={tUi("addSource")}>{tUi("addSource")}</span></span>
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        <span key={tUi("matchersAreGroupedPerSourceWithinASource")}>{tUi("matchersAreGroupedPerSourceWithinASource")}</span></p>
                      {selectedChannel.sources.length === 0 ? (
                        <p className="rounded-md border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">
                          <span key={tUi("noSourcesConfiguredAddAtLeastOneSource")}>{tUi("noSourcesConfiguredAddAtLeastOneSource")}</span></p>
                      ) : (
                        <div className="space-y-10">
                        {selectedChannel.sources.map((source, sourceIndex) => (
                          <div
                            key={source.localId}
                            className="space-y-4 rounded-md border p-4 bg-gray-50"
                            ref={sourceIndex === selectedChannel.sources.length - 1 ? lastSourceRef : null}
                          >
                              <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
                                <div className="grid gap-2">
                                  <Label><span key={tUi("sourceLabel")}>{tUi("sourceLabel")}</span></Label>
                                  <Input
                                    value={source.label}
                                    placeholder={tUi("eGGoogleAds")}
                                    onChange={(event) =>
                                      handleSourceLabelChange(
                                        selectedChannel.localId,
                                        source.localId,
                                        event.target.value,
                                      )
                                    }
                                  />
                                  <p className="text-xs text-muted-foreground">
                                    <span><span key={tUi("internalID")}>{tUi("internalID")}</span></span> <code>{source.slug}</code>
                                  </p>
                                </div>
                                <div className="flex justify-end">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleRemoveSource(selectedChannel.localId, source.localId)}
                                    disabled={selectedChannel.sources.length === 1}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" /> <span><span key={tUi("remove")}>{tUi("remove")}</span></span>
                                  </Button>
                                </div>
                              </div>

                              <div className="space-y-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-muted-foreground"><span key={tUi("matchers")}>{tUi("matchers")}</span></span>
                                    {detectMatcherMode(source.matchers) === "grouped" && (
                                      <Badge variant="outline" className="text-xs"><span key={tUi("oRGroups")}>{tUi("oRGroups")}</span></Badge>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {detectMatcherMode(source.matchers) === "legacy" && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleAddMatcher(selectedChannel.localId, source.localId)}
                                      >
                                        <Plus className="mr-2 h-4 w-4" /> <span><span key={tUi("addMatcher")}>{tUi("addMatcher")}</span></span>
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleAddMatcherGroup(selectedChannel.localId, source.localId)}
                                      title={tUi("addAnAlternativeRuleTheCurrentMatchersStay")}
                                    >
                                      <Plus className="mr-2 h-4 w-4" /> <span><span key={tUi("addORGroup")}>{tUi("addORGroup")}</span></span>
                                    </Button>
                                    {detectMatcherMode(source.matchers) === "grouped" && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setDisableGroupsTarget({ channelLocalId: selectedChannel.localId, sourceLocalId: source.localId })}
                                        title={tUi("mergeTheBaseRuleAndAllGroupsInto")}
                                      >
                                        <span key={tUi("disableGroups")}>{tUi("disableGroups")}</span></Button>
                                    )}
                                  </div>
                                </div>

                                {renderSourceMatchers(selectedChannel.localId, source)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="h-px w-full bg-muted" />

                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteChannel(selectedChannel.localId)}
                      disabled={selectedChannel.isSpecial || selectedChannel.isFallback}
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> <span><span key={tUi("deleteChannel")}>{tUi("deleteChannel")}</span></span>
                    </Button>
                  </div>
                </div>
              </ScrollArea>
            ) : (
              <div className="flex h-full items-center justify-center rounded-md border border-dashed">
                <p className="text-sm text-muted-foreground"><span key={tUi("selectAChannelToEditItsSettings")}>{tUi("selectAChannelToEditItsSettings")}</span></p>
              </div>
            )}
          </div>
        )}
        <UrlParamAliasesDialog
          integrationId={integrationId}
          open={isAliasDialogOpen}
          onOpenChange={setIsAliasDialogOpen}
          value={paramAliasSets}
          onSaved={(next) => {
            setParamAliasSets(next)
            onConfigSaved?.()
          }}
        />
        <DisableGroupsConfirm
          open={disableGroupsTarget !== null}
          onOpenChange={(open) => {
            if (!open) setDisableGroupsTarget(null)
          }}
          onConfirm={() => {
            if (disableGroupsTarget) {
              handleDisableGroupedMode(disableGroupsTarget.channelLocalId, disableGroupsTarget.sourceLocalId)
            }
            setDisableGroupsTarget(null)
          }}
        />
        <DialogFooter>
          <div className="flex w-full items-center justify-between gap-2">
            <Button
              variant="outline"
              className="gap-2"
              disabled={isLoading || isSaving || isResetting || loadFailed}
              onClick={() => setIsAliasDialogOpen(true)}
            >
              <SlidersHorizontal className="h-4 w-4" />
              <span><span key={tUi("uRLParameterAliases")}>{tUi("uRLParameterAliases")}</span></span>
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
              <Button onClick={handleSave} disabled={isSaving || loadFailed}>
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> <span key={tUi("saving")}>{tUi("saving")}</span></>
                ) : (
                  tUi("saveChanges")
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
